import { makeObservable, observable, action, toJS } from 'mobx';
import SettingsRequests from 'Utils/Requests/SettingsRequests';
import React from 'react';
import UsersRequests from "Utils/Requests/UsersRequests";
import { CloseModal, ShowAlert, ShowModal } from "Utils/CommonFunctions";
import ClearAllProtocolModal from 'Components/ProtocolView/clearAllProtocolModal';
import ProtocolRecordsStore from 'Stores/ProtocolRecords/protocolRecordsStore';
import SetMaxFileSizeModal from 'Components/OtherSettings/SetMaxFileSizeModal';
import { WebErrorCodeEnum } from 'Utils/Requests/WebEnums';
import SmrTableStoreV2 from 'Stores/Common/Table/SmrTableStoreV2';
import Helper from 'Utils/Helper';
import Enumerable from "linq";

class OtherSettingsStore {
    currentServerSettings;
    /** @type {OtherSettings} */
    currentLocalSettings;
    settingsErrors;

    /** @type {DocumentsUpdateProcessState} */
    restrictionsUpdateProgress;
    restrictions;
    selectedRestrictionsIds;
    clientLogsInfosLoading;
    clientLogsInfos;
    clientLogsUserIdOpened;
    clientLogsSubDirectoriesListFilter;
    clientLogsTableStore;
    silentCleanPeriods;
    clientLogsCleanPeriods;
    protocolArchiveDirectory;
    fileModalInitiator;
    settingsLoading;
    usersReloadState;
    syncSyncedStartResponseWait;
    maxFileSize;
    /** @type {DocumentsUpdateProcessState} */
    bigFileUpdateProgress;
    renderStore;

    constructor() {
        makeObservable(this, {
            currentServerSettings: observable,
            currentLocalSettings: observable,
            settingsErrors: observable,
            restrictionsUpdateProgress: observable,
            restrictions: observable,
            selectedRestrictionsIds: observable,
            settingsLoading: observable,
            clientLogsInfosLoading: observable,
            clientLogsInfos: observable,
            clientLogsUserIdOpened: observable,
            clientLogsSubDirectoriesListFilter: observable,
            clientLogsTableStore: observable,
            silentCleanPeriods: observable,
            protocolArchiveDirectory: observable,
            usersReloadState: observable,
            syncSyncedStartResponseWait: observable,
            maxFileSize: observable,
            bigFileUpdateProgress: observable,
            renderStore: observable,
        });
        this.currentLocalSettings = {
            externalSmrUrl: '',
            silentCleanPeriod: 30,
            restoreLogicalDeletedUsers: false,
            showTechInfo: false,
            clientLogsCleanPeriod: 30,
            protocolArchivationEnabled: false,
            protocolArchivePeriod: 60,
            protocolSavePeriod: 30,
            supportEmail: '',
            mcDisableSslChainValidation: false,
            emailInforming: {
                email: '',
                protocol: false,
                stoplist: false,
                from: '',
                smtpServer: '',
                smtpPort: null,
                login: '',
                password: '',
                note: '',
                checkRevocation: true,
            }
        };
        this.settingsErrors = {
            supportEmailError: false,
            emailInformingError: false,
            emailInformingFromError: false
        };
        this.restrictionsUpdateProgress = {};
        this.restrictions = [];
        this.selectedRestrictionsIds = [];
        this.silentCleanPeriods = [];
        this.clientLogsCleanPeriods = [];
        this.protocolArchiveDirectory = '';
        this.settingsLoading = false;
        this.clientLogsInfosLoading = false;
        this.clientLogsInfos = [];
        this.clientLogsUserIdOpened = -1;
        this.clientLogsSubDirectoriesListFilter = {};
        this.clientLogsTableStore = new SmrTableStoreV2({ diffBetweenWindowAndTableRowsContainerHeight: 370 });

        this.usersReloadState = null;
        this.syncSyncedStartResponseWait = false;
        this.maxFileSize = 2000;
        this.bigFileUpdateProgress = {
            inProcess: false,
            isMeeting: false,
            progressCurrent: 0,
            progressTotal: 0
        };
        this.skipShowAlertsTypeSuccess = false;
        this.renderStore = {};
    }

    GetSettingsChanged() {
        if (this.currentServerSettings === null)
            return false;

        return JSON.stringify(this.currentServerSettings) !== JSON.stringify(this.currentLocalSettings);
    }

    GetSettings = action(() => {
        this.settingsLoading = true;

        return SettingsRequests.GetOtherSettings()
            .then(action(values => {
                this.clientLogsCleanPeriods = values.clientLogsCleanPeriods;
                this.silentCleanPeriods = values.silentCleanPeriods;
                this.protocolArchiveDirectory = values.protocolArchiveDirectory;
                this.maxFileSize = values.maxFileSize;
                this.bigFileUpdateProgress = values.bigFilesUpdate;
                this.restrictionsUpdateProgress = values.restrictionsUpdate;

                this.restrictions = values.restrictions;
                this.selectedRestrictionsIds = values.restrictions.filter(it => it.disabled).map(it => it.id);
                this.currentServerSettings = values.otherSettings;
                this.currentLocalSettings = Helper.CloneObject(values.otherSettings);
            }))
            .catch(action(() => this.currentServerSettings = null))
            .finally(action(() => this.settingsLoading = false));
    });

    GetClientLogsSubDirectoriesList = action(() => {
        this.clientLogsInfosLoading = true;

        let filter = {
            ... this.clientLogsSubDirectoriesListFilter,
            start: this.clientLogsTableStore.GetCurrentPageStartRecordIndex(),
            length: this.clientLogsTableStore.elementsPerPage
        };

        SettingsRequests.GetClientLogsSubDirectoriesList(filter)
            .then(action(values => {
                if (this.clientLogsTableStore.HandlePagination(values.countTotal, () => this.GetClientLogsSubDirectoriesList()) == false)
                    return;

                this.clientLogsInfos = values.userInfos;
                this.clientLogsInfosLoading = false;
            }));
    });

    SetOtherSettingsState = action(usersReloadState => {
        this.usersReloadState = usersReloadState;
    });

    /**
     * @param {DocumentsUpdateProcessState} data 
     */
    SetBigFileUpdateState(data) {
        this.bigFileUpdateProgress = data;
    }

    /**
     * @param {DocumentsUpdateProcessState} data 
     */
    SetRestrictionUpdateState(data) {
        this.restrictionsUpdateProgress = data;
    }

    SaveRestrictions = action(() => {
        this.settingsLoading = true;

        /** @type {SaveRestrictionSettingsRequest} */
        let restrictionsToSend = {
            switchedOn: this.selectedRestrictionsIds,
            switchedOff: Enumerable.from(this.restrictions.map(it => it.id)).except(Enumerable.from(this.selectedRestrictionsIds)).toArray()
        };

        return SettingsRequests.SaveRestrictionSettings(restrictionsToSend)
            .then(action(values => {
                if (!values.error) {
                    ShowAlert('success', 'Успешно запущена пересинхронизация файлов');
                    this.restrictions = values.restrictions;
                    this.SetRestrictionUpdateState(values.restrictionUpdate);
                } else {
                    ShowAlert('error', 'Не удалось запустить пересинхронизацию файлов. Перейдите в раздел "Протоколирование"');
                }
            }))
            .finally(action(() => this.settingsLoading = false));
    });

    SaveSettingsWithBlock = action(() => {
        if (this.settingsLoading)
            return;

        this.settingsLoading = true;
        return this.SaveSettings()
            .finally(action(() => this.settingsLoading = false))
    });

    SaveSettings = action(async () => {
        return await SettingsRequests.SaveOtherSettings(this.currentLocalSettings)
            .then(action((values) => {
                if (!values.error) {
                    if (this.skipShowAlertsTypeSuccess != true)
                        ShowAlert('success', 'Настройки успешно сохранены.');

                    this.settingsErrors.supportEmailError = false;
                    this.settingsErrors.emailInformingError = false;
                    this.settingsErrors.emailInformingFromError = false;
                    this.currentServerSettings = structuredClone(toJS(this.currentLocalSettings));
                } else {
                    ShowAlert('error',
                        <div>
                            При сохранении настроек произошла ошибка.<br />
                            {(values.error.errorText == null ? 'Убедитесь что СМР работает и повторите попытку.' : values.error.errorText)}
                        </div>
                    );

                    switch (values.error.errorCode) {
                        case WebErrorCodeEnum.SupportEmailValidationError:
                            this.settingsErrors.supportEmailError = true;
                            break;
                        case WebErrorCodeEnum.InformingEmailValidationError:
                            this.settingsErrors.emailInformingError = true;
                            break;
                        case WebErrorCodeEnum.InformingFromEmailValidationError:
                            this.settingsErrors.emailInformingFromError = true;
                            break;
                    }
                }

                return values;
            }));
    });

    SetSupportEmail = action((value) => {
        this.settingsErrors.supportEmailError = false;
        this.currentLocalSettings.supportEmail = value;
    });

    SetInformingValue(key, value) {
        switch (key) {
            case 'email':
                this.settingsErrors.emailInformingError = false;
                this.currentLocalSettings.emailInforming.email = value;
                break;
            case 'protocol':
                this.currentLocalSettings.emailInforming.protocol = value;
                break;
            case 'stoplist':
                this.currentLocalSettings.emailInforming.stoplist = value;
                break;
            case 'from':
                this.settingsErrors.emailInformingFromError = false;
                this.currentLocalSettings.emailInforming.from = value;
                break;
            case 'smtpServer':
                this.currentLocalSettings.emailInforming.smtpServer = value;
                break;
            case 'smtpPort':
                this.currentLocalSettings.emailInforming.smtpPort = value ? value : null;
                break;
            case 'login':
                this.currentLocalSettings.emailInforming.login = value;
                break;
            case 'password':
                this.currentLocalSettings.emailInforming.password = value;
                break;
            case 'note':
                this.currentLocalSettings.emailInforming.note = value;
                break;
            case 'checkRevocation':
                this.currentLocalSettings.emailInforming.checkRevocation = value;
                break;
        }
    };

    SetMaxFileSize = action((/** @type {number} */ val) => {
        this.maxFileSize = val;
    });

    SendTestEmail = action(() => {
        this.settingsLoading = true;

        this.skipShowAlertsTypeSuccess = true; // BUG 53029 . чтобы не мигал сначала инфо alert затем alert ошибка при ошибках ввода настроек email

        this.SaveSettings()
            .then(action(result => {
                if (result.error) {
                    return result;
                }

                return SettingsRequests.SendTestEmail()
                    .then(result => {
                        if (result.isSuccess)
                            ShowAlert('success', 'Письмо отправлено успешно');
                        else
                            ShowAlert(
                                result.isWarning ? 'warning' : 'error',
                                (<div>
                                    {result.error}<br />
                                    Тестовое письмо не может быть отправлено.
                                </div>)
                            );

                        return result;
                    });
            }))
            .finally(action(() => {
                this.skipShowAlertsTypeSuccess = false;
                this.settingsLoading = false;
            }));
    });

    SetShowTechInfo = action((value) => {
        this.currentLocalSettings.showTechInfo = value;
    });

    SetProtocolArchivationEnabled = action((value) => {
        this.currentLocalSettings.protocolArchivationEnabled = value;
    });

    SetSelectedSilentCleanPeriod = action((value) => {
        this.currentLocalSettings.silentCleanPeriod = value;
    });

    SetRestoreLogicalDeletedUsers = action((value) => {
        this.currentLocalSettings.restoreLogicalDeletedUsers = value;
    });

    SetProtocolSavePeriod = action((value) => {
        this.currentLocalSettings.protocolSavePeriod = value;
    });

    SetProtocolArchivePeriod = action((value) => {
        this.currentLocalSettings.protocolArchivePeriod = value;
    });

    SetExternalSmrUrl(value) {
        this.currentLocalSettings.externalSmrUrl = value;
    }

    SetClientLogsCleanPeriod = action((value) => {
        this.currentLocalSettings.clientLogsCleanPeriod = value;
    });

    SetWebSessionIdleTimeoutMinutes = action((value) => {
        this.currentLocalSettings.webSessionIdleTimeoutMinutes = value;
    });

    SetAdminMaxLoginAttempts = action((value) => {
        this.currentLocalSettings.adminMaxLoginAttempts = value;
    });

    SyncSyncedUsers = action(() => {
        if (this.syncSyncedStartResponseWait)
            return;

        this.syncSyncedStartResponseWait = true;

        SettingsRequests.SyncSyncedUsers({ allProductModes: true })
            .then(action((values) => {
                if (!values.error) {
                    this.usersReloadState = {
                        inProcess: true,
                        usersCount: 0,
                        currentStateOfUsersQueue: [],
                        progress: {}
                    };
                    ShowAlert('success', "Успешно запущена синхронизация активированных пользователей");
                } else {
                    ShowAlert('error', 'Не удалось запустить синхронизацию пользователей. Перейдите в раздел "Протоколирование"');
                }
            }))
            .finally(action(() => {
                this.syncSyncedStartResponseWait = false;
            }));
    })

    StopSyncSyncedProcess() {
        UsersRequests.StopSyncSyncedProcess();
    }

    ShowClearAllProtocolModal() {
        ShowModal({
            canBeClosed: true,
            header: 'Очистка протокола пользователей',
            className: 'clear-protocol-modal',
            children: <ClearAllProtocolModal />,
            buttons: [{
                content: 'Нет',
                closer: true
            }, {
                content: 'Да',
                closer: true,
                onClick: () => ProtocolRecordsStore.ClearProtocolRecords()
            }]
        });
    }

    ShowSetMaxFileSizeModal() {
        this.renderStore.selectedMaxFileSize = this.maxFileSize;
        var startUpdateMaxFileSizeRequest = false;

        ShowModal({
            canBeClosed: true,
            header: 'Пересинхронизация файлов',
            children: <SetMaxFileSizeModal
                getMaxFileSize={_ => this.renderStore.selectedMaxFileSize}
                setMaxFileSize={val => this.renderStore.selectedMaxFileSize = val} />,
            buttons: [{
                content: 'Отменить',
                onClick: close => {
                    if (startUpdateMaxFileSizeRequest)
                        return;

                    close();
                }
            }, {
                content: 'Запустить',
                className: 'blue-button',
                onClick: async (close) => {
                    if (startUpdateMaxFileSizeRequest)
                        return;

                    try {
                        startUpdateMaxFileSizeRequest = true;

                        let result = await SettingsRequests.SaveMaxFileSizeSetting({ maxFileSize: this.renderStore.selectedMaxFileSize });
                        if (!result.error) {
                            this.SetMaxFileSize(this.renderStore.selectedMaxFileSize);
                            this.SetBigFileUpdateState(result.bigFileUpdate);
                            if (result.bigFileUpdate.inProcess)
                                ShowAlert('success', "Успешно запущена пересинхронизация файлов.");
                        }
                        else {
                            ShowAlert('error', result.error.errorText);
                        }
                    } finally {
                        startUpdateMaxFileSizeRequest = false;
                    }

                    close();
                }
            }]
        });
    }
}

export default new OtherSettingsStore();